/**
 * R.H.I.E. AI Recommendation Card
 * Kişiselleştirilmiş öneri kartları
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Play, Brain, Clock, Zap, ChevronRight, X, Sparkles, Target } from 'lucide-react';
import { useRHIE } from './RHIEProvider';

const categoryColors = {
  'gece-seremonisi': '#7C3AED',
  'sabah-rituel': '#FFA94D',
  'odak-guc': '#2DE2E6',
  'icsel-guc': '#E879F9',
  'zihin-sakinligi': '#34D399',
  'berraklik': '#60A5FA',
  default: '#7C3AED'
};

export default function AIRecommendationCard({ 
  recommendation, 
  showReason = true,
  compact = false,
  onAccept,
  onReject,
  className = ''
}) {
  const navigate = useNavigate();
  const { startRitual, engine } = useRHIE();
  
  if (!recommendation || !recommendation.session) return null;
  
  const { session, reason, score } = recommendation;
  const categoryKey = session.category?.key || session.category_key || 'default';
  const color = categoryColors[categoryKey] || categoryColors.default;
  
  const handlePlay = () => {
    if (onAccept) onAccept(recommendation);
    else if (engine) engine.acceptRecommendation(recommendation);
    
    startRitual(session);
    navigate('/panel/telkinler');
  };
  
  const handleReject = (e) => {
    e.stopPropagation();
    if (onReject) onReject(recommendation);
    else if (engine) engine.rejectRecommendation(recommendation);
  };
  
  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer group ${className}`}
        style={{ 
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.05)'
        }}
        onClick={handlePlay}
      >
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${color}40, ${color}20)` }}
        >
          <Brain className="w-5 h-5" style={{ color }} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-white text-sm font-medium truncate">{session.title}</h4>
          <p className="text-white/40 text-xs truncate">{reason}</p>
        </div>
        
        <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{
        background: 'linear-gradient(135deg, rgba(18, 18, 30, 0.8), rgba(18, 18, 30, 0.6))',
        backdropFilter: 'blur(16px)',
        border: `1px solid ${color}30`
      }}
    >
      {/* AI Badge */}
      <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full"
        style={{ background: `${color}20` }}
      >
        <Target className="w-3 h-3" style={{ color }} />
        <span className="text-xs font-medium" style={{ color }}>AI Öneri</span>
      </div>
      
      {/* Reject button */}
      {onReject && (
        <button
          onClick={handleReject}
          className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <X className="w-3 h-3 text-white/40" />
        </button>
      )}
      
      {/* Gradient overlay */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{ background: `linear-gradient(135deg, ${color}30, transparent)` }}
      />
      
      <div className="relative p-5 pt-12">
        {/* Session info */}
        <div className="flex items-center gap-4 mb-4">
          <div 
            className="w-16 h-16 rounded-xl flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${color}40, ${color}15)`,
              boxShadow: `0 10px 30px -10px ${color}50`
            }}
          >
            <Sparkles className="w-7 h-7" style={{ color }} />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-lg mb-1 line-clamp-2">
              {session.title}
            </h3>
            <div className="flex items-center gap-3 text-xs text-white/40">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {session.duration_display || '10 dk'}
              </span>
              <span className="flex items-center gap-1">
                <Zap className="w-3 h-3 text-amber-400" />
                +{session.base_xp || 15} ZP
              </span>
            </div>
          </div>
        </div>
        
        {/* AI Reason */}
        {showReason && reason && (
          <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
            <p className="text-white/50 text-sm flex items-center gap-2">
              <Brain className="w-4 h-4 flex-shrink-0" style={{ color }} />
              {reason}
            </p>
          </div>
        )}
        
        {/* Action button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handlePlay}
          className="w-full py-3 rounded-xl flex items-center justify-center gap-2 text-white font-medium transition-all"
          style={{ 
            background: `linear-gradient(135deg, ${color}, ${color}99)`,
            boxShadow: `0 4px 20px -5px ${color}60`
          }}
        >
          <Play className="w-5 h-5" />
          Başla
        </motion.button>
        
        {/* Score indicator (subtle) */}
        {score && (
          <div className="mt-3 flex items-center justify-center gap-2 text-white/20 text-xs">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i}
                  className="w-1 h-1 rounded-full"
                  style={{ 
                    background: i < Math.ceil(score / 20) ? color : 'rgba(255,255,255,0.2)' 
                  }}
                />
              ))}
            </div>
            <span>Uyumluluk</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
